/* UniBuy™ · Sub-Suppliers tile panel.

   Shown inside the UniBuy™ sidebar so buyers see a clear second
   purchasing path. Clicking a tile fires an outbound
   PunchOutSetupRequest. If the sub-supplier has `requires_ad_login=true`
   (e.g. Grainger Advantage), an Azure-AD-branded interstitial appears
   first to capture the buyer's corporate identity — that UPN is then
   embedded in the outbound cXML so the partner can apply
   company-specific pricing.
*/
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Loader2, Store, ShieldCheck, ArrowLeftRight, Globe } from "lucide-react";
import MicrosoftADSignInDialog from "./MicrosoftADSignInDialog";
import PunchoutLaunchOverlay from "./PunchoutLaunchOverlay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function resolveLogo(s) {
  return s?.logo_path ? `${BACKEND_URL}${s.logo_path}` : s?.logo_url || null;
}

export default function SubSupplierTiles({ session }) {
  const [suppliers, setSuppliers] = useState([]);
  const [launching, setLaunching] = useState(null);
  const [adDialog, setAdDialog] = useState({ open: false, supplier: null });

  useEffect(() => {
    (async () => {
      try {
        // Country-target the tile list — Avantor (NL) and Grainger (US)
        // only render for personas in their authorized country.
        const params = session?.country_code ? `?country_code=${encodeURIComponent(session.country_code)}` : "";
        const { data } = await axios.get(`${API}/infoshop/sub-suppliers${params}`);
        setSuppliers(data.sub_suppliers || []);
      } catch {
        setSuppliers([]);
      }
    })();
  }, [session?.country_code]);

  const doLaunch = async (s, adContext = null) => {
    if (!session) {
      toast.error("UniBuy™ session not ready");
      return;
    }
    setLaunching(s.id);
    try {
      const originSessionId = new URLSearchParams(window.location.search).get(
        "punchout_session"
      );
      const { data } = await axios.post(
        `${API}/punchout/sub-supplier/${s.id}/launch`,
        null,
        {
          params: {
            network_id: session.network_id,
            company_name: session.company_name,
            country_code: session.country_code,
            origin_session_id: originSessionId || undefined,
            ad_token: adContext?.ad_token || undefined,
          },
        }
      );
      if (data.used_mock_fallback) {
        toast.message(
          `Couldn't reach ${s.name}'s real PunchOut endpoint — falling back to the bundled mock catalog so the demo continues.`
        );
      }
      // Land the buyer on the partner webshop or the bundled mock.
      const relPath = data.start_page_path;
      if (relPath) {
        window.location.href = relPath;
        return;
      }
      const url = data.start_page_url;
      try {
        const u = new URL(url);
        if (u.origin === window.location.origin) {
          window.location.href = `${u.pathname}${u.search}`;
        } else {
          // Real partner — opens in a new tab so the buyer can come
          // back to UniBuy™ via the back-link in the mock UX or via
          // the cXML BrowserFormPost return.
          window.open(url, "_blank", "noopener");
        }
      } catch {
        window.open(url, "_blank", "noopener");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 401 && detail.toLowerCase().includes("microsoft")) {
        // Backend told us we need AD sign-in — show the dialog and retry.
        setAdDialog({ open: true, supplier: s });
      } else {
        toast.error(detail || `Could not launch ${s.name}`);
      }
    } finally {
      setLaunching(null);
    }
  };

  const onTileClick = (s) => {
    // OCI uses a browser form-POST into the vendor's SAP login URL —
    // no server-to-server POSR handshake. Open the launcher page in
    // a new tab; it auto-submits the form and the vendor's catalog
    // takes over.
    if ((s.protocol || "").toUpperCase() === "OCI") {
      const qp = new URLSearchParams({
        network_id: session?.network_id || "",
        company_name: session?.company_name || "",
        country_code: session?.country_code || "",
      }).toString();
      window.open(`${API}/infoshop/sub-suppliers/${s.id}/launch-oci?${qp}`, "_blank", "noopener");
      return;
    }
    if (s.requires_ad_login) {
      setAdDialog({ open: true, supplier: s });
    } else {
      doLaunch(s);
    }
  };

  if (!suppliers.length) return null;

  // Partition by wire protocol — cXML PunchOut vs OCI Dynamic Catalog.
  // Each protocol gets its own labelled group so buyers and demo
  // audiences can clearly see the difference. Scales to N suppliers
  // per protocol (e.g. Lyreco OCI enabled country-by-country, or any
  // new OCI partner an admin adds — tiles appear automatically).
  const isOCI = (s) => (s.protocol || "cXML").toUpperCase() === "OCI";
  const cxmlSuppliers = suppliers.filter((s) => !isOCI(s));
  const ociSuppliers = suppliers.filter(isOCI);

  const renderTile = (s) => {
    const isLaunching = launching === s.id;
    const logo = resolveLogo(s);
    const oci = isOCI(s);
    return (
      <button
        key={s.id}
        onClick={() => onTileClick(s)}
        disabled={!!launching}
        data-testid={`sub-supplier-tile-${s.code}`}
        className={`group text-left rounded-lg border bg-slate-50 hover:bg-white transition-all p-3 disabled:opacity-50 disabled:cursor-wait ${
          oci ? "border-slate-200 hover:border-violet-500" : "border-slate-200 hover:border-[#0072CE]"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          {logo ? (
            <img
              src={logo}
              alt={s.name}
              className="h-7 w-7 rounded-md bg-white border border-slate-200 object-contain p-0.5"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className={`h-7 w-7 rounded-md border font-bold flex items-center justify-center text-[11px] ${
              oci
                ? "bg-violet-600/10 border-violet-600/20 text-violet-600"
                : "bg-[#0072CE]/10 border-[#0072CE]/20 text-[#0072CE]"
            }`}>
              {s.code.slice(0, 2)}
            </div>
          )}
          {s.requires_ad_login && (
            <span
              title="Microsoft AD sign-in required"
              className="inline-flex items-center gap-1 text-[8px] uppercase tracking-wider font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1 py-0.5"
            >
              <ShieldCheck size={8} /> MS AD
            </span>
          )}
          {isLaunching ? (
            <Loader2 size={12} className={`animate-spin ml-auto ${oci ? "text-violet-600" : "text-[#0072CE]"}`} />
          ) : (
            <ExternalLink size={11} className={`ml-auto ${oci ? "text-slate-400 group-hover:text-violet-600" : "text-slate-400 group-hover:text-[#0072CE]"}`} />
          )}
        </div>
        <div className="text-xs font-display font-semibold text-slate-900 leading-tight">
          {s.name}
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2 leading-snug">
          {s.tagline || s.description}
        </div>
        <div className="mt-1.5">
          <span
            data-testid={`sub-supplier-protocol-pill-${s.code}`}
            className={`inline-flex items-center text-[8px] uppercase tracking-wider font-bold rounded px-1 py-0.5 border ${
              oci
                ? "text-violet-700 bg-violet-50 border-violet-200"
                : "text-sky-700 bg-sky-50 border-sky-200"
            }`}
          >
            {oci ? "OCI Dynamic Catalog" : "cXML PunchOut"} · {s.currency}
          </span>
        </div>
      </button>
    );
  };

  return (
    <>
      <div
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        data-testid="sub-supplier-tiles"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Store size={14} className="text-[#0072CE]" />
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Browse Sub-Suppliers
          </span>
        </div>
        <div className="p-3 space-y-3">
          {cxmlSuppliers.length > 0 && (
            <div data-testid="sub-supplier-group-cxml">
              <div
                className="flex items-center gap-2 px-0.5"
                data-testid="sub-supplier-group-header-cxml"
              >
                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">
                  <ArrowLeftRight size={9} /> cXML PunchOut
                </span>
                <span className="text-[9px] font-mono text-slate-400">
                  {cxmlSuppliers.length} supplier{cxmlSuppliers.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {cxmlSuppliers.map(renderTile)}
              </div>
            </div>
          )}
          {ociSuppliers.length > 0 && (
            <div data-testid="sub-supplier-group-oci">
              <div
                className="flex items-center gap-2 px-0.5"
                data-testid="sub-supplier-group-header-oci"
              >
                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                  <Globe size={9} /> OCI Dynamic Catalog
                </span>
                <span className="text-[9px] font-mono text-slate-400">
                  {ociSuppliers.length} supplier{ociSuppliers.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {ociSuppliers.map(renderTile)}
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 leading-snug">
          Pick up where you left off — items return to this UniBuy™ cart with auto-created UniBuy™ Part Numbers, whether via cXML PunchOut or OCI Dynamic Catalog.
        </div>
      </div>

      <MicrosoftADSignInDialog
        open={adDialog.open}
        onOpenChange={(o) => setAdDialog({ open: o, supplier: o ? adDialog.supplier : null })}
        supplier={adDialog.supplier}
        session={session}
        onSignedIn={async (ctx) => {
          const s = adDialog.supplier;
          setAdDialog({ open: false, supplier: null });
          await doLaunch(s, ctx);
        }}
      />
      {/* Fullscreen progress curtain — keeps the buyer informed during
          the cXML handshake + window.location redirect to the partner. */}
      <PunchoutLaunchOverlay
        supplier={launching ? suppliers.find((x) => x.id === launching) : null}
      />
    </>
  );
}
